/**
 * Card Component
 *
 * A versatile card component supporting both shorthand and compound APIs.
 * Provides flexible content composition with progressive enhancement.
 *
 * Features:
 * - Three visual variants (default, accent, minimal)
 * - Collapsible functionality with WCAG compliance
 * - Progressive enhancement for SEO and accessibility
 * - Flexible header/body composition
 *
 * Follows Constellation v10 patterns for compound components.
 */

// Import all components first so we can use them in compound object
import { Card } from './Card';
import { CardHeader } from './CardHeader';
import { CardBody } from './CardBody';
import { CardTitle } from './CardTitle';
import { CardIcon } from './CardIcon';

// Export individual components for named imports
export { Card };
export { CardHeader };
export { CardBody };
export { CardTitle };
export { CardIcon };

// Export all types
export type {
  CardProps,
  CardHeaderProps,
  CardBodyProps,
  CardTitleProps,
  CardIconProps,
  CardVariant,
  CardSize,
  TitleSize,
  CardResponsiveVariant,
} from './types';

// Create compound component following FullscreenOverlay pattern
// This allows both shorthand and compound API usage:
//
// Shorthand: <Card title="..." icon={...}>{children}</Card>
// Compound:  <Card.Header><Card.Title>...</Card.Title></Card.Header>
const CardCompound = Object.assign(Card, {
  Header: CardHeader,
  Body: CardBody,
  Title: CardTitle,
  Icon: CardIcon,
});

// Export as default for compound usage
export default CardCompound;
